import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, TrendingUp, TrendingDown, Minus, Dice5, RefreshCw, Shield, DollarSign, Clock, Target, Download, Receipt, CalendarCheck, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useRiskIntelligenceContract, type RiskKPI } from '@/hooks/useRiskIntelligence';
import { useRkgComparison } from '@/hooks/useRkgComparison';
import { useEvmContrato } from '@/hooks/useEvmContrato';
import { useEvmCurvaS } from '@/hooks/useEvmCurvaS';
import { EvmCurvaSChart } from './EvmCurvaSChart';

import { RkgComparisonPanel } from '@/components/risk/RkgComparisonPanel';
import { McProbCard } from './McProbCard';
import { McTrendChart } from './McTrendChart';
import { PredictiveAlertsBanner } from './PredictiveAlertsBanner';
import { FinancierExportButton } from './FinancierExportButton';
import { useEmpMonteCarloLatest, useEmpMonteCarloHistory } from '@/hooks/useEmpMonteCarlo';
import { useEmpAlertasPreditivos } from '@/hooks/useEmpAlertasPreditivos';
import { useEmpRelatorioFinanciador } from '@/hooks/useEmpRelatorioFinanciador';
import { useMcProjectionState } from '@/hooks/useMcProjectionState';
import { McProjectionBadge } from './McProjectionBadge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Props {
  contratoId: string;
  contratoNome: string;
  canEdit: boolean;
}

import { fmtCurrencyInt as formatCurrency } from '@/lib/formatters';

const TendenciaIcon: React.FC<{ t: string | null }> = ({ t }) => {
  if (t === 'melhorando') return <TrendingDown className="h-4 w-4 text-success" />;
  if (t === 'piorando') return <TrendingUp className="h-4 w-4 text-destructive" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

const exportContractCSV = (kpis: RiskKPI[], name: string, mcProbOnTime?: number | null) => {
  const headers = ['Período', 'Score Risco', 'Prob. Cumprimento (%)', 'Var. Custo (%)', 'Exposição Financeira', 'Tendência'];
  const rows = kpis.map((k, i) => [
    k.periodo, k.score_risco ?? '',
    // First row (latest): use MC prob_on_time SSOT; fallback to legacy for historical rows
    i === 0 && mcProbOnTime != null ? Math.round(mcProbOnTime) : (k.prob_atraso != null ? Math.round(100 - k.prob_atraso) : ''),
    k.variacao_custo_pct != null ? k.variacao_custo_pct.toFixed(1) : '', k.exposicao_financeira ?? '', k.tendencia ?? '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `risk-${name}-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
  toast.success('Relatório exportado');
};

export const EmpRiskIntelligenceView: React.FC<Props> = ({ contratoId, contratoNome, canEdit }) => {
  const { loading, kpis, monteCarlo, mcRunning, runMonteCarlo, refresh } = useRiskIntelligenceContract(contratoId);
  const { enabled: rkgEnabled, rkgData, backtestMetrics, useFallback } = useRkgComparison(contratoId);
  const { evmSnapshot } = useEvmContrato(contratoId);
  const { curvaSData } = useEvmCurvaS(contratoId);
  

  // F4 Forecast MC data
  const { data: mcLatest } = useEmpMonteCarloLatest(contratoId);
  const { data: mcHistory = [] } = useEmpMonteCarloHistory(contratoId);
  const mcState = useMcProjectionState(contratoId);
  // F6 Predictive alerts
  const { data: predAlertas = [] } = useEmpAlertasPreditivos(contratoId);
  // F7 Financier report data
  const { data: relFinanciador } = useEmpRelatorioFinanciador(contratoId);

  console.debug('[EVM:RISK]', evmSnapshot ? 'EVM_SOURCE_SELECTED' : 'EVM_DATA_EMPTY');

  const latest = kpis.length > 0 ? kpis[0] : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* F6: Predictive Alerts Banner */}
      <PredictiveAlertsBanner alertas={predAlertas} />

      {/* KPI Cards */}
      <div className={cn(
        "grid grid-cols-1 sm:grid-cols-2 gap-4",
        evmSnapshot ? "lg:grid-cols-3 xl:grid-cols-5" : "lg:grid-cols-3 xl:grid-cols-5"
      )}>
        <Card className="border-l-4 border-l-destructive/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Score de Risco</span>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-end gap-2">
              <span className={cn(
                "text-2xl font-bold",
                (latest?.score_risco ?? 0) > 70 ? "text-destructive" :
                (latest?.score_risco ?? 0) > 40 ? "text-warning" : "text-success"
              )}>
                {latest?.score_risco ?? '—'}
              </span>
              <span className="text-xs text-muted-foreground mb-1">/100</span>
              {latest && <TendenciaIcon t={latest.tendencia} />}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Prob. Cumprimento</span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Clock className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs max-w-[220px]">
                    <p className="font-medium mb-1">Fonte: Monte Carlo F4</p>
                    <p>Probabilidade de concluir no prazo contratual, calculada por simulação probabilística ({mcLatest?.n_iteracoes?.toLocaleString() ?? '—'} iterações).</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-end gap-2">
              {mcLatest ? (
                <span className={cn(
                  "text-2xl font-bold",
                  mcLatest.prob_on_time >= 70 ? "text-success" :
                  mcLatest.prob_on_time >= 40 ? "text-warning" : "text-destructive"
                )}>
                  {mcLatest.prob_on_time.toFixed(0)}%
                </span>
              ) : (
                <span className="text-sm text-muted-foreground italic">Aguardando simulação</span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-primary/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Variação Custo</span>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-end gap-2">
              <span className={cn(
                "text-2xl font-bold",
                (latest?.variacao_custo_pct ?? 0) > 10 ? "text-destructive" :
                (latest?.variacao_custo_pct ?? 0) > 5 ? "text-warning" : "text-foreground"
              )}>
                {latest?.variacao_custo_pct != null ? `${latest.variacao_custo_pct.toFixed(1)}%` : '—'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-accent/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Exposição Financeira</span>
              <Target className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold text-foreground">
              {latest?.exposicao_financeira != null ? formatCurrency(latest.exposicao_financeira) : '—'}
            </div>
          </CardContent>
        </Card>

        {/* A3: Previsão de Término — P50 MC prioritário, fallback determinístico */}
        <Card className="border-l-4 border-l-warning/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground font-medium">Previsão de Término</span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CalendarCheck className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs max-w-[240px]">
                    {mcLatest ? (
                      <p>Fonte: <strong>Monte Carlo P50</strong> (mediana probabilística, {mcLatest.n_iteracoes.toLocaleString()} iterações)</p>
                    ) : relFinanciador?.forecast_termino ? (
                      <p>Fonte: <strong>Projeção determinística</strong> (SPI médio móvel)</p>
                    ) : (
                      <p>Aguardando dados de simulação ou projeção.</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {(() => {
              const p50Date = mcLatest?.p50_data;
              const detDate = relFinanciador?.forecast_termino;
              const displayDate = p50Date || detDate;

              if (!displayDate) {
                return <span className="text-sm text-muted-foreground italic">Aguardando simulação</span>;
              }

              let formattedDate: string;
              try {
                formattedDate = format(new Date(displayDate), 'dd/MM/yyyy', { locale: ptBR });
              } catch {
                formattedDate = displayDate;
              }

              // Calculate delay in days vs contract end (if available from relFinanciador)
              const contratoFim = relFinanciador?.data_fim_prevista;
              let delayDays: number | null = null;
              if (contratoFim) {
                try {
                  const fimMs = new Date(contratoFim).getTime();
                  const forecastMs = new Date(displayDate).getTime();
                  delayDays = Math.round((forecastMs - fimMs) / (1000 * 60 * 60 * 24));
                } catch { /* silent */ }
              }

              return (
                <div>
                  <p className="text-xl font-bold text-foreground">{formattedDate}</p>
                  {delayDays != null && (
                    <Badge
                      variant={delayDays <= 0 ? 'default' : delayDays <= 30 ? 'secondary' : 'destructive'}
                      className="mt-1.5 text-xs"
                    >
                      {delayDays <= 0
                        ? 'No prazo'
                        : delayDays <= 30
                          ? `+${delayDays}d — Risco moderado`
                          : `+${delayDays}d — Atraso projetado`
                      }
                    </Badge>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* EVM Conditional Cards */}
        {evmSnapshot && evmSnapshot.vac != null && (
          <Card className={cn(
            "border-l-4",
            evmSnapshot.vac >= 0 ? "border-l-success/50" : "border-l-destructive/50"
          )}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">VAC</span>
                <Receipt className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold">
                <span className={evmSnapshot.vac >= 0 ? 'text-success' : 'text-destructive'}>
                  {formatCurrency(evmSnapshot.vac)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {evmSnapshot.vac >= 0 ? 'Economia prevista' : 'Estouro previsto'}
              </p>
            </CardContent>
          </Card>
        )}

        {evmSnapshot && evmSnapshot.eac != null && evmSnapshot.bac > 0 && (
          <Card className="border-l-4 border-l-muted-foreground/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">BAC vs EAC</span>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-muted-foreground">BAC</span>
                    <span className="font-medium">{formatCurrency(evmSnapshot.bac)}</span>
                  </div>
                  <Progress value={100} className="h-1.5" />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-muted-foreground">EAC</span>
                    <span className={cn("font-medium", evmSnapshot.eac > evmSnapshot.bac ? "text-destructive" : "text-success")}>
                      {formatCurrency(evmSnapshot.eac)}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(150, (evmSnapshot.eac / evmSnapshot.bac) * 100)}
                    className={cn("h-1.5", evmSnapshot.eac > evmSnapshot.bac ? "[&>div]:bg-destructive" : "[&>div]:bg-success")}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {evmSnapshot.eac > evmSnapshot.bac
                    ? `+${(((evmSnapshot.eac / evmSnapshot.bac) - 1) * 100).toFixed(0)}% acima do orçamento`
                    : `${((1 - (evmSnapshot.eac / evmSnapshot.bac)) * 100).toFixed(0)}% abaixo do orçamento`
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Monte Carlo Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div className="flex items-center gap-2">
            <Dice5 className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Simulação Monte Carlo — Risco</CardTitle>
            <McProjectionBadge state={mcState} triggeredBy={mcLatest?.triggered_by} versaoLogica={mcLatest?.versao_logica} />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
              Atualizar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportContractCSV(kpis, contratoNome, mcLatest?.prob_on_time)}
              disabled={kpis.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <FinancierExportButton contratoId={contratoId} contratoNome={contratoNome} data={relFinanciador ?? null} />
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={runMonteCarlo} disabled={mcRunning}>
                    <Dice5 className={cn("h-4 w-4 mr-2", mcRunning && "animate-spin")} />
                    {mcRunning ? 'Recalculando...' : 'Forçar Recálculo'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {monteCarlo ? (
            <div className="space-y-4">
              {/* Scenarios */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ScenarioCard
                  label="Melhor Caso"
                  prazo={monteCarlo.melhor_prazo}
                  custo={monteCarlo.melhor_custo}
                  prob={monteCarlo.melhor_prob}
                  variant="success"
                />
                <ScenarioCard
                  label="Cenário Provável"
                  prazo={monteCarlo.provavel_prazo}
                  custo={monteCarlo.provavel_custo}
                  prob={monteCarlo.provavel_prob}
                  variant="warning"
                />
                <ScenarioCard
                  label="Pior Caso"
                  prazo={monteCarlo.pior_prazo}
                  custo={monteCarlo.pior_custo}
                  prob={monteCarlo.pior_prob}
                  variant="destructive"
                />
              </div>

              {/* Summary stats from resultado */}
              {monteCarlo.resultado && (
                <div className="bg-muted/50 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">P50 Prazo</span>
                    <p className="font-semibold">{(monteCarlo.resultado as any).p50_duration ?? '—'} dias</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">P80 Prazo</span>
                    <p className="font-semibold">{(monteCarlo.resultado as any).p80_duration ?? '—'} dias</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Prob. Atraso</span>
                    <p className="font-semibold text-destructive">{(monteCarlo.resultado as any).prob_delay ?? '—'}%</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Prob. Estouro</span>
                    <p className="font-semibold text-destructive">{(monteCarlo.resultado as any).prob_overbudget ?? '—'}%</p>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Última atualização: {monteCarlo.completed_at
                  ? new Date(monteCarlo.completed_at).toLocaleString('pt-BR')
                  : 'N/A'
                } · {monteCarlo.n_simulations.toLocaleString()} iterações
                {monteCarlo.resultado && (
                  <> · Calibrado com {(monteCarlo.resultado as any).baseline_samples ?? 0} amostras de baseline
                  + {(monteCarlo.resultado as any).netuno_indicators ?? 0} indicadores reais</>
                )}
                <span className="ml-2 text-primary/70">· Atualização automática ativa</span>
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <Dice5 className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground mb-2">
                Nenhuma simulação de risco executada para este contrato.
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                A simulação será executada automaticamente quando houver dados de medição ou apontamentos.
              </p>
              {canEdit && (
                <Button size="sm" variant="outline" onClick={runMonteCarlo} disabled={mcRunning}>
                  <Dice5 className={cn("h-4 w-4 mr-1", mcRunning && "animate-spin")} />
                  {mcRunning ? 'Recalculando...' : 'Forçar Recálculo'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* RKG Comparison Panel */}
      {rkgEnabled && rkgData && (
        <RkgComparisonPanel
          rkgData={rkgData}
          legacyData={monteCarlo ? {
            p50_prazo: (monteCarlo.resultado as any)?.p50_duration ?? monteCarlo.provavel_prazo,
            p80_prazo: (monteCarlo.resultado as any)?.p80_duration ?? monteCarlo.pior_prazo,
            prob_atraso: (monteCarlo.resultado as any)?.prob_delay ?? (monteCarlo.resultado as any)?.prob_atraso,
            prob_estouro: (monteCarlo.resultado as any)?.prob_overbudget ?? (monteCarlo.resultado as any)?.prob_estouro,
          } : null}
          backtestMetrics={backtestMetrics}
          useFallback={useFallback}
        />
      )}

      {/* F4: Forecast MC — Probability + Risk Drivers */}
      {mcLatest && <McProbCard mc={mcLatest} />}

      {/* F4: MC Trend — P10/P50/P90 evolution */}
      {mcHistory.length >= 2 && <McTrendChart history={mcHistory} />}

      {/* Curva S Física — removed: PanoramaProgressoChart in header is the single source */}

      {/* Curva S Financeira — EVM Chart (conditional on EPC-QTD data) */}
      {curvaSData && <EvmCurvaSChart data={curvaSData} />}

      {/* Historical KPIs */}
      {kpis.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Histórico de Indicadores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Período</th>
                    <th className="pb-2 font-medium text-muted-foreground">Score</th>
                    <th className="pb-2 font-medium text-muted-foreground">Prob. Cumprimento</th>
                    <th className="pb-2 font-medium text-muted-foreground">Var. Custo</th>
                    <th className="pb-2 font-medium text-muted-foreground">Exposição</th>
                    <th className="pb-2 font-medium text-muted-foreground">Tendência</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.map((k, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 font-medium">{k.periodo}</td>
                      <td className="py-2">
                        <Badge variant={
                          (k.score_risco ?? 0) > 70 ? 'destructive' :
                          (k.score_risco ?? 0) > 40 ? 'secondary' : 'outline'
                        }>
                          {k.score_risco ?? '—'}
                        </Badge>
                      </td>
                      <td className="py-2">{k.prob_atraso != null ? `${Math.round(100 - k.prob_atraso)}%` : '—'}</td>
                      <td className="py-2">{k.variacao_custo_pct != null ? `${k.variacao_custo_pct.toFixed(1)}%` : '—'}</td>
                      <td className="py-2">{k.exposicao_financeira != null ? formatCurrency(k.exposicao_financeira) : '—'}</td>
                      <td className="py-2"><TendenciaIcon t={k.tendencia} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {kpis.length === 0 && !monteCarlo && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground mb-1">
              Nenhum dado de risco disponível para este contrato.
            </p>
            <p className="text-xs text-muted-foreground">
              Indicadores serão gerados automaticamente à medida que apontamentos de campo forem registrados.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Scenario Card ─────────────────────────────────────────────────
const ScenarioCard: React.FC<{
  label: string;
  prazo: number | null;
  custo: number | null;
  prob: number | null;
  variant: 'success' | 'warning' | 'destructive';
}> = ({ label, prazo, custo, prob, variant }) => {
  const borderColor = {
    success: 'border-success/40',
    warning: 'border-warning/40',
    destructive: 'border-destructive/40',
  }[variant];

  const textColor = {
    success: 'text-success',
    warning: 'text-warning',
    destructive: 'text-destructive',
  }[variant];

  return (
    <div className={cn("border rounded-lg p-4 space-y-2", borderColor)}>
      <p className={cn("text-sm font-semibold", textColor)}>{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Prazo</span>
          <span className="font-medium">{prazo != null ? `${Math.round(prazo)} dias` : '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Custo</span>
          <span className="font-medium">{custo != null ? formatCurrency(custo) : '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Percentil</span>
          <span className="font-medium">P{prob != null ? Math.round(prob) : '—'}</span>
        </div>
      </div>
    </div>
  );
};
